import React, { useState, useEffect, useRef } from "react";
import "./CadastroImoveis.css";
import axios from "axios";
import ThemeProvider from "../theme/TemaMuiProvider";
import { Box, Button, TablePagination, TextField, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import muiTheme from "../theme/muiTheme";
import {
  FaSearch,
  FaFilter,
  FaCopy,
  FaFileCsv,
  FaTrash,
  FaEye,
  FaTimes,
  FaEdit,
  FaInfoCircle,
  FaChevronDown,
  FaChevronUp,
  FaGlobe,
  FaSave,
  FaPaperclip,
} from "react-icons/fa";

import { jwtDecode } from "jwt-decode";

import { copiarTexto } from "../utils/clipboard";

// Formatação Data
function formatDateToInput(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d)) return "";
  return d.toISOString().split("T")[0];
}

// MÁSCARA CEP
function maskCEP(v) {
  return v
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .substring(0, 9);
}
// Converte automaticamente qualquer texto para MAIÚSCULO
function upper(value) {
  return (value || "").toUpperCase();
}

// BUSCAR CEP VIA API
async function buscarCEP(cep) {
  const apenasNumeros = cep.replace(/\D/g, "");
  if (apenasNumeros.length !== 8) return null;
  try {
    const res = await fetch(`https://viacep.com.br/ws/${apenasNumeros}/json/`);
    const data = await res.json();
    if (data.erro) return null;
    return {
      endereco: data.logradouro,
      bairro: data.bairro,
      cidade: data.localidade,
      uf: data.uf,
    };
  } catch {
    return null;
  }
}

// Mascara Matrícula
function maskNumeroMatricula(v) {
  if (!v) return "";
  v = v.replace(/\D/g, "");
  return v.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

// MÁSCARA VALOR (R$) - somente para digitação
function maskMoney(value) {
  if (!value) return "";
  let v = value.toString().replace(/\D/g, "");
  v = (parseInt(v, 10) / 100).toFixed(2) + "";
  v = v.replace(".", ",");
  v = v.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return "R$ " + v;
}

// FORMATA VALOR JÁ SALVO/VINDO DO BACKEND
function formatMoneyBR(value) {
  if (value === null || value === undefined || value === "") return "";

  let numberValue;

  if (typeof value === "number") {
    numberValue = value;
  } else {
    const raw = String(value).trim();

    // remove "R$", espaços e separadores de milhar; converte vírgula para ponto
    const normalized = raw.replace(/\s/g, "").replace("R$", "").replace(/\./g, "").replace(",", ".");

    numberValue = Number(normalized);
  }

  if (Number.isNaN(numberValue)) return "";

  return numberValue.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

// DESMASCARA VALOR
function unmaskMoney(value) {
  if (value === null || value === undefined || value === "") return null;

  return Number(String(value).replace(/\s/g, "").replace("R$", "").replace(/\./g, "").replace(",", "."));
}

// MÁSCARA COORDENADAS
function maskCoordinate(v) {
  return v.replace(/[^0-9\-.]/g, "");
}

/**
 * ✅ AJUSTE: MÁSCARA METRAGEM (pt-BR) — robusta (4 casas decimais)
 * - Aceita decimal com vírgula OU ponto (ex: 98765.17 -> 98.765,17)
 * - Marca milhar com ponto
 * - Decimal exibido com vírgula
 * - No blur/salvar/carregar força 4 casas
 */
const METRAGEM_DECIMAIS = 4;

function onlyDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function stripLeadingZeros(intDigits) {
  if (!intDigits) return "";
  const stripped = intDigits.replace(/^0+(?=\d)/, "");
  return stripped || "0";
}

function formatThousands(intDigits) {
  return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function maskMetragemTypingBR(input) {
  const s = String(input ?? "").trim();
  if (!s) return "";

  const cleaned = s.replace(/[^\d,]/g, "");
  const hasComma = cleaned.includes(",");

  const parts = cleaned.split(",");
  const rawInt = parts[0] ?? "";
  const rawDec = parts.slice(1).join("");

  let intDigits = rawInt.replace(/\D/g, "");
  intDigits = stripLeadingZeros(intDigits);

  if (!intDigits) return "";

  const intFormatted = formatThousands(intDigits);

  if (!hasComma) {
    return intFormatted;
  }

  const decDigits = rawDec.replace(/\D/g, "").slice(0, 4);

  return `${intFormatted},${decDigits}`;
}

function maskMetragem4BR(input) {
  const s = String(input ?? "").trim();
  if (!s) return "";

  const cleaned = s.replace(/[^\d,]/g, "");
  const hasComma = cleaned.includes(",");

  const parts = cleaned.split(",");
  const rawInt = parts[0] ?? "";
  const rawDec = parts.slice(1).join("");

  let intDigits = rawInt.replace(/\D/g, "");
  intDigits = stripLeadingZeros(intDigits);

  if (!intDigits) return "";

  const intFormatted = formatThousands(intDigits);

  if (!hasComma) {
    return `${intFormatted},0000`;
  }

  const decDigits = rawDec.replace(/\D/g, "").slice(0, 4).padEnd(4, "0");

  return `${intFormatted},${decDigits}`;
}

function maskMetragemBR(input, forceFixedDecimals = false) {
  return forceFixedDecimals ? maskMetragem4BR(input) : maskMetragemTypingBR(input);
}

/**
 * ✅ AJUSTE: normaliza o imóvel vindo do backend (que pode vir como 98765.17)
 * e garante que na UI sempre fique "98.765,0000".
 * (Somente campos de metragem)
 */
function normalizeImovelForUI(item) {
  const safe = item || {};

  const geometriaTexto = String(safe.geometriaRegular ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  const geometria = safe.geometriaRegular === true || safe.geometriaRegular === 1 || geometriaTexto === "sim" ? "Sim" : "Não";

  return {
    ...safe,
    geometriaRegular: geometria,
    areaTerreno: maskMetragem4BR(safe.areaTerreno ?? ""),
    areaConstruida: maskMetragem4BR(safe.areaConstruida ?? ""),
    metrosFrente: maskMetragem4BR(safe.metrosFrente ?? ""),
    metrosFundo: maskMetragem4BR(safe.metrosFundo ?? ""),
    metrosLadoDireito: maskMetragem4BR(safe.metrosLadoDireito ?? ""),
    metrosLadoEsquerdo: maskMetragem4BR(safe.metrosLadoEsquerdo ?? ""),
  };
}

function CadastroImoveis() {
  const [form, setForm] = useState({
    codigoMatricula: "",
    apelido: "",
    tipoDocumentoImovel: "",
    numeroMatricula: "",
    cidadeRegistro: "",
    cartorioRegistro: "",
    nomeProprietarioMatricula: "",
    status: "",
    tipo: "",
    nomeProprietarioConceito: "",
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    uf: "",
    linkGoogleMaps: "",
    latitude: "",
    longitude: "",
    nomeVendedor: "",
    dataCompraEscritura: "",
    valorCompraEscritura: "",
    dataCompraContrato: "",
    valorCompraContrato: "",
    areaTerreno: "",
    areaConstruida: "",
    geometriaRegular: "Não",
    metrosFrente: "",
    metrosFundo: "",
    metrosLadoDireito: "",
    metrosLadoEsquerdo: "",
    prazoResolutiva: "",
    observacao: "",
  });

  const [editandoId, setEditandoId] = useState(null);

  // Modo visualização
  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [visualizandoId, setVisualizandoId] = useState(null);

  // controla quando campos vazios ficam vermelhos
  const [mostrarPendencias, setMostrarPendencias] = useState(false);

  // ✅ GRID MINIMIZÁVEL (persistência)
  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("imoveisGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("imoveisGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  const camposObrigatorios = [
    "apelido",
    "tipoDocumentoImovel",
    "nomeProprietarioMatricula",
    "endereco",
    "bairro",
    "cidade",
    "uf",
    "nomeVendedor",
    "dataCompraEscritura",
    "dataCompraContrato",
    "valorCompraContrato",
    "areaConstruida",
    "areaTerreno",
    "metrosFrente",
  ];

  const camposDesejados = [
    "nomeProprietarioConceito",
    "cep",
    "numero",
    "complemento",
    "valorCompraEscritura",
    "geometriaRegular",
    "metrosFundo",
    "metrosLadoDireito",
    "metrosLadoEsquerdo",
    "observacao",
    "latitude",
    "longitude",
  ];

  const camposSinalizaveis = [...camposObrigatorios, ...camposDesejados];
  const estaVazio = (v) => !String(v ?? "").trim();

  const classeCampoPendente = (campo) => {
    if (!mostrarPendencias) return "";
    if (!camposSinalizaveis.includes(campo)) return "";
    return estaVazio(form[campo]) ? "campo-pendente" : "";
  };

  const campoPadraoClasse =
    "w-full rounded-md border border-white/30 bg-white/5 text-[color:var(--ig-t-ffffff)] px-4 py-3 outline-none transition-colors duration-200 focus:border-white/50 focus:bg-white/5 placeholder:text-[color:var(--ig-t-9ca3af)]";

  const montarClasseCampo = (campo) => `${campoPadraoClasse} ${classeCampoPendente(campo)}`.trim();

  const erroCampoMUI = (campo) => {
    if (!mostrarPendencias) return false;
    if (!camposSinalizaveis.includes(campo)) return false;
    return estaVazio(form[campo]);
  };

  const muiCampoSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      display: "flex",
      alignItems: "center",

      "& fieldset": {
        borderColor: "var(--ig-b-555555)",
      },

      "&:hover fieldset": {
        borderColor: "var(--ig-b-777777)",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#2ea8ff",
        borderWidth: 2,
      },

      "&.Mui-error fieldset": {
        borderColor: "#ef4444 !important",
        borderWidth: 2,
      },

      "&.Mui-disabled fieldset": {
        borderColor: "var(--ig-b-555555)",
      },
    },

    "& .MuiOutlinedInput-input": {
      padding: "16.5px 14px",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "var(--ig-t-ffffff)",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#2ea8ff",
    },

    "& .MuiInputLabel-root.Mui-error": {
      color: "#ef4444 !important",
    },

    "& .MuiInputLabel-root.Mui-disabled": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiSvgIcon-root": {
      color: "var(--ig-t-ffffff)",
    },
  };

  const menuPropsDark = {
    MenuProps: {
      PaperProps: {
        sx: {
          backgroundColor: "var(--ig-f-111827) !important",
          color: "var(--ig-t-ffffff)",
        },
      },
    },
  };

  // GED
  const [arquivos, setArquivos] = useState([]);
  const [modalGED, setModalGED] = useState(false);
  const [idSelecionadoGED, setIdSelecionadoGED] = useState(null);
  const [gedPendentes, setGedPendentes] = useState([]);

  const inputGedRef = useRef(null);

  const abrirSeletorGED = () => {
    if (!inputGedRef.current) {
      console.error("Input de upload GED não está disponível.");
      alert("Não foi possível abrir a seleção de arquivos GED.");
      return;
    }

    inputGedRef.current.click();
  };

  const selecionarArquivosGED = (event) => {
    const novosArquivos = Array.from(event.target.files || []);

    if (novosArquivos.length === 0) {
      return;
    }

    setGedPendentes((arquivosAtuais) => {
      const arquivosUnificados = [...arquivosAtuais, ...novosArquivos];

      const arquivosSemDuplicidade = arquivosUnificados.filter(
        (arquivo, index, lista) =>
          index === lista.findIndex((item) => item.name === arquivo.name && item.size === arquivo.size && item.lastModified === arquivo.lastModified),
      );

      return arquivosSemDuplicidade;
    });

    // Permite selecionar novamente o mesmo arquivo depois.
    event.target.value = "";
  };

  const baixarArquivoGED = async (arquivoId, nome) => {
    try {
      const resp = await fetch(`/api/imoveis/${idSelecionadoGED}/ged/${arquivoId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!resp.ok) throw new Error("Falha no download");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Erro ao baixar arquivo.");
    }
  };

  const visualizarArquivoGED = async (imovelId, arquivoId, nomeArquivo = "") => {
    if (!imovelId || !arquivoId) {
      alert("Arquivo inválido para visualização.");
      return;
    }

    const nome = (nomeArquivo || arquivoId || "").toLowerCase();

    const extensoesVisualizaveis = [".pdf", ".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg"];

    const podeVisualizar = extensoesVisualizaveis.some((ext) => nome.endsWith(ext));

    if (!podeVisualizar) {
      const confirmar = window.confirm(
        `Visualização indisponível.\n\nO arquivo "${nomeArquivo}" não possui visualização integrada no ImobGest.\n\nPara visualizar este tipo de arquivo, é necessário baixar e abrir em um aplicativo compatível.\n\nDeseja baixar agora?`,
      );

      if (confirmar) {
        baixarArquivoGED(arquivoId, nomeArquivo);
      }

      return;
    }

    try {
      const resp = await fetch(`/api/imoveis/${imovelId}/ged/${encodeURIComponent(arquivoId)}/visualizar`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) {
        throw new Error("Erro ao visualizar arquivo GED.");
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (err) {
      console.error(err);
      alert("Erro ao visualizar arquivo GED.");
    }
  };

  const enviarGED = async (id, files) => {
    if (!id) {
      console.error("ID do imóvel não informado para envio do GED.");
      return false;
    }

    if (!Array.isArray(files) || files.length === 0) {
      return true;
    }

    const formData = new FormData();

    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await fetch(`/api/imoveis/${id}/ged`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let mensagem = "Erro ao enviar arquivos GED.";

        try {
          const data = await response.json();

          mensagem = data?.erro || data?.mensagem || mensagem;
        } catch {
          // O backend pode retornar conteúdo sem JSON.
        }

        console.error("Falha no envio GED:", {
          status: response.status,
          mensagem,
        });

        return false;
      }

      return true;
    } catch (error) {
      console.error("Erro ao enviar GED:", error);
      return false;
    }
  };

  const abrirGED = async (id) => {
    setIdSelecionadoGED(id);
    setModalGED(true);
    try {
      const resp = await fetch(`/api/imoveis/${id}/ged`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const dados = await resp.json();
      if (Array.isArray(dados)) setArquivos(dados);
      else setArquivos([]);
    } catch (err) {
      console.error("Erro ao carregar arquivos GED:", err);
      setArquivos([]);
    }
  };

  const excluirArquivoGED = async (arquivoId) => {
    if (!window.confirm("Deseja excluir este arquivo?")) return;

    try {
      const resp = await fetch(`/api/imoveis/${idSelecionadoGED}/ged/${arquivoId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!resp.ok) throw new Error("Erro ao excluir GED");

      setArquivos((old) => old.filter((a) => String(a.id || a.nome) !== String(arquivoId)));

      alert("Arquivo GED excluído!");
    } catch (err) {
      console.error(err);
      alert("Erro ao excluir arquivo GED.");
    }
  };
  // Combos
  const [cartorios, setCartorios] = useState([]);
  const [cidadesRegistro, setCidadesRegistro] = useState([]);
  const [proprietariosMatricula, setProprietariosMatricula] = useState([]);
  const [proprietariosConceito, setProprietariosConceito] = useState([]);
  const [statusList, setStatusList] = useState([]);

  // Listagem
  const [registros, setRegistros] = useState([]);
  const [filtrados, setFiltrados] = useState([]);
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(true);

  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [matriculasPorPagina, setMatriculasPorPagina] = useState(5);

  // Utilitário
  const pick = (data, keyFallbacks = []) => {
    if (Array.isArray(data)) return data;
    for (const k of keyFallbacks) {
      if (Array.isArray(data?.[k])) return data[k];
    }
    return [];
  };

  const aplicarListaUI = (lista) => {
    const arr = Array.isArray(lista) ? lista : [];
    const normalizada = arr.map(normalizeImovelForUI);
    setRegistros(normalizada);
    setFiltrados(normalizada);
  };

  // Carrega combos + imóveis
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    async function carregarDados() {
      try {
        const [resCartorios, resCidades, resProprietarios, resStatus, resImoveis] = await Promise.all([
          axios.get("/api/cartorios", { headers }),
          axios.get("/api/cidades", { headers }),
          axios.get("/api/proprietarios", { headers }),
          axios.get("/api/status", { headers }),
          axios.get("/api/imoveis", { headers }),
        ]);

        const cartoriosArr = pick(resCartorios.data, ["cartorios"]).map((c) => c?.nome ?? c);
        setCartorios(cartoriosArr);

        const cidadesArr = pick(resCidades.data, ["cidades"]).map((c) => c?.nome ?? c);
        setCidadesRegistro(cidadesArr);

        const proprietariosArr = pick(resProprietarios.data, ["proprietarios"]).map((p) => p?.nome ?? p);
        setProprietariosMatricula(proprietariosArr);
        setProprietariosConceito(proprietariosArr);

        const statusArr = pick(resStatus.data, ["status", "statuses", "itens", "lista"]).map((s) => s?.nome ?? s?.descricao ?? s?.status ?? s);
        setStatusList(statusArr);

        aplicarListaUI(resImoveis.data);
      } catch (err) {
        console.error("Erro ao carregar dados iniciais:", err);
      } finally {
        setCarregando(false);
      }
    }

    carregarDados();
  }, []);

  // Filtro
  const handleBusca = (e) => {
    const valor = e.target.value;
    setBusca(valor);

    setPaginaAtual(1);

    if (!valor.trim()) {
      setFiltrados(registros);
      return;
    }

    const termos = valor
      .toLowerCase()
      .split(/[;,]/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const filtradosList = registros.filter((item) => {
      const textoItem = Object.entries(item)
        .map(([, v]) => (v !== null && v !== undefined ? String(v).toLowerCase() : ""))
        .join(" ");
      return termos.every((termo) => textoItem.includes(termo));
    });

    setFiltrados(filtradosList);
  };

  const limparFiltro = () => {
    setBusca("");
    setFiltrados(registros);
    setPaginaAtual(1);
  };

  const copiarTabela = async () => {
    const texto = filtrados
      .map((r) =>
        [
          r.codigoMatricula ?? "",
          r.apelido ?? "",
          r.numeroMatricula ?? "",
          r.cidadeRegistro ?? "",
          r.cartorioRegistro ?? "",
          r.nomeProprietarioMatricula ?? "",
          r.status ?? "",
          r.tipo ?? "",
          r.nomeProprietarioConceito ?? "",
          r.cep ?? "",
          r.endereco ?? "",
          r.numero ?? "",
          r.complemento ?? "",
          r.bairro ?? "",
          r.cidade ?? "",
          r.uf ?? "",
          r.latitude ?? "",
          r.longitude ?? "",
          r.nomeVendedor ?? "",
          r.dataCompraEscritura ?? "",
          formatMoneyBR(r.valorCompraEscritura) ?? "",
          r.dataCompraContrato ?? "",
          formatMoneyBR(r.valorCompraContrato) ?? "",
          r.areaTerreno ?? "",
          r.areaConstruida ?? "",
          r.geometriaRegular ?? "",
          r.metrosFrente ?? "",
          r.metrosFundo ?? "",
          r.metrosLadoDireito ?? "",
          r.metrosLadoEsquerdo ?? "",
          r.observacao ?? "",
        ].join("\t"),
      )
      .join("\n");

    const ok = await copiarTexto(texto);
    alert(ok ? "Registros copiados para a área de transferência!" : "Não foi possível copiar.");
  };

  const exportarCSV = () => {
    const BOM = "\uFEFF";

    const header = [
      "Código Matrícula",
      "Apelido",
      "Número Matrícula",
      "Cidade Registro",
      "Cartório Registro",
      "Nome Proprietário Matrícula",
      "Status",
      "Tipo",
      "Nome Proprietário Conceito",
      "CEP",
      "Endereço",
      "Número",
      "Complemento",
      "Bairro",
      "Cidade",
      "UF",
      "Latitude",
      "Longitude",
      "Nome Vendedor",
      "Data Compra Escritura",
      "Valor Compra Escritura",
      "Data Compra Contrato",
      "Valor Compra Contrato",
      "Área Terreno (m²)",
      "Área Construída (m²)",
      "Geometria Regular",
      "Metros Frente (m)",
      "Metros Fundo (m)",
      "Metros Lado Direito (m)",
      "Metros Lado Esquerdo (m)",
      "Observação",
    ];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const excelText = (value) => `="${String(value ?? "")}"`;

    const linhas = filtrados.map((r) =>
      [
        r.codigoMatricula ?? "",
        r.apelido ?? "",
        excelText(r.numeroMatricula ?? ""),
        r.cidadeRegistro ?? "",
        r.cartorioRegistro ?? "",
        r.nomeProprietarioMatricula ?? "",
        r.status ?? "",
        r.tipo ?? "",
        r.nomeProprietarioConceito ?? "",
        excelText(r.cep ?? ""),
        r.endereco ?? "",
        excelText(r.numero ?? ""),
        r.complemento ?? "",
        r.bairro ?? "",
        r.cidade ?? "",
        r.uf ?? "",
        excelText(r.latitude ?? ""),
        excelText(r.longitude ?? ""),
        r.nomeVendedor ?? "",
        r.dataCompraEscritura ?? "",
        excelText(formatMoneyBR(r.valorCompraEscritura)),
        r.dataCompraContrato ?? "",
        excelText(formatMoneyBR(r.valorCompraContrato)),
        excelText(maskMetragemBR(r.areaTerreno ?? "", true)),
        excelText(maskMetragemBR(r.areaConstruida ?? "", true)),
        r.geometriaRegular ?? "",
        excelText(maskMetragemBR(r.metrosFrente ?? "", true)),
        excelText(maskMetragemBR(r.metrosFundo ?? "", true)),
        excelText(maskMetragemBR(r.metrosLadoDireito ?? "", true)),
        excelText(maskMetragemBR(r.metrosLadoEsquerdo ?? "", true)),
        r.observacao ?? "",
      ].map(csvEscape),
    );

    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "imoveis.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const CAMPOS_MAIUSCULOS = ["apelido", "endereco", "numero", "bairro", "cidade", "uf", "complemento", "nomeVendedor", "observacao"];

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: CAMPOS_MAIUSCULOS.includes(name) ? value.toUpperCase() : value,
    }));
  };

  // Handlers metragem
  const handleMetragemChange = (campo) => (e) => {
    setForm((prev) => ({
      ...prev,
      [campo]: maskMetragemTypingBR(e.target.value),
    }));
  };

  const handleMetragemBlur = (campo) => (e) => {
    setForm((prev) => ({
      ...prev,
      [campo]: maskMetragem4BR(e.target.value),
    }));
  };

  // carrega dados no formulário (VISUALIZAR/EDITAR)
  const carregarRegistroNoFormulario = (item) => {
    const it = normalizeImovelForUI(item);

    console.log("Registro carregado no formulário:", {
      id: it.id,
      geometriaRegular: it.geometriaRegular,
      metrosFrente: it.metrosFrente,
      metrosFundo: it.metrosFundo,
      metrosLadoDireito: it.metrosLadoDireito,
      metrosLadoEsquerdo: it.metrosLadoEsquerdo,
    });

    setForm({
      ...it,
      dataCompraEscritura: formatDateToInput(it.dataCompraEscritura),
      dataCompraContrato: formatDateToInput(it.dataCompraContrato),
      prazoResolutiva: formatDateToInput(it.prazo_resolutiva),

      valorCompraEscritura:
        it.valorCompraEscritura !== null && it.valorCompraEscritura !== undefined && it.valorCompraEscritura !== ""
          ? formatMoneyBR(it.valorCompraEscritura)
          : "",

      valorCompraContrato:
        it.valorCompraContrato !== null && it.valorCompraContrato !== undefined && it.valorCompraContrato !== "" ? formatMoneyBR(it.valorCompraContrato) : "",

      apelido: it.apelido || "",

      tipoDocumentoImovel: it.tipoDocumentoImovel || (it.numeroMatricula || it.cidadeRegistro || it.cartorioRegistro ? "MATRÍCULA" : ""),

      numeroMatricula: it.numeroMatricula || "",
      cidadeRegistro: it.cidadeRegistro || "",
      cartorioRegistro: it.cartorioRegistro || "",
      nomeProprietarioMatricula: it.nomeProprietarioMatricula || "",
      status: it.status || "",
      tipo: it.tipo || "",
      nomeProprietarioConceito: it.nomeProprietarioConceito || "",
      nomeVendedor: it.nomeVendedor || "",
      cep: it.cep || "",
      endereco: it.endereco || "",
      numero: it.numero || "",
      complemento: it.complemento || "",
      bairro: it.bairro || "",
      cidade: it.cidade || "",
      uf: it.uf || "",
      linkGoogleMaps: it.link_google_maps || "",
      latitude: it.latitude || "",
      longitude: it.longitude || "",
      observacao: it.observacao || "",
      geometriaRegular:
        String(it.geometriaRegular ?? "")
          .trim()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase() === "sim"
          ? "Sim"
          : "Não",
      areaTerreno: it.areaTerreno ?? "",
      areaConstruida: it.areaConstruida ?? "",
      metrosFrente: it.metrosFrente ?? "",
      metrosFundo: it.metrosFundo ?? "",
      metrosLadoDireito: it.metrosLadoDireito ?? "",
      metrosLadoEsquerdo: it.metrosLadoEsquerdo ?? "",
    });

    setMostrarPendencias(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // VISUALIZAR
  const visualizarRegistro = (item) => {
    carregarRegistroNoFormulario(item);
    setVisualizandoId(item.id);
    setModoVisualizacao(true);
    setEditandoId(null);
  };

  // EDITAR
  const editarRegistro = (item) => {
    carregarRegistroNoFormulario(item);
    setEditandoId(item.id);
    setModoVisualizacao(false);
    setVisualizandoId(null);
    setMostrarPendencias(true);
  };

  const cancelarFormulario = () => {
    setForm({
      codigoMatricula: "",
      apelido: "",
      tipoDocumentoImovel: "",
      numeroMatricula: "",
      cidadeRegistro: "",
      cartorioRegistro: "",
      nomeProprietarioMatricula: "",
      status: "",
      tipo: "",
      nomeProprietarioConceito: "",
      cep: "",
      endereco: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      uf: "",
      latitude: "",
      longitude: "",
      nomeVendedor: "",
      dataCompraEscritura: "",
      valorCompraEscritura: "",
      dataCompraContrato: "",
      valorCompraContrato: "",
      areaTerreno: "",
      areaConstruida: "",
      geometriaRegular: "Não",
      metrosFrente: "",
      metrosFundo: "",
      metrosLadoDireito: "",
      metrosLadoEsquerdo: "",
      prazoResolutiva: "",
      observacao: "",
    });

    setEditandoId(null);
    setModoVisualizacao(false);
    setVisualizandoId(null);

    setMostrarPendencias(false);

    setGedPendentes([]);
    if (inputGedRef.current) {
      inputGedRef.current.value = "";
    }
    setArquivos([]);
    setModalGED(false);
    setIdSelecionadoGED(null);
  };

  // Excluir
  const excluirRegistro = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este imóvel?")) return;

    try {
      console.log("Excluindo imóvel ID:", id);

      await axios.delete(`/api/imoveis/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      alert("Imóvel excluído com sucesso!");

      setRegistros((prev) => prev.filter((r) => r.id !== id));
      setFiltrados((prev) => prev.filter((r) => r.id !== id));

      if (editandoId === id || visualizandoId === id) {
        cancelarFormulario();
      }
    } catch (err) {
      console.error("Erro ao excluir imóvel:", err?.response?.data || err);

      const mensagem = err?.response?.data?.erro || err?.response?.data?.message || "Erro ao excluir imóvel.";

      alert(mensagem);
    }
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (modoVisualizacao) return;

    setMostrarPendencias(true);

    const camposObrigatoriosAtuais = [
      ...camposObrigatorios,
      ...(form.tipoDocumentoImovel === "MATRÍCULA" ? ["numeroMatricula", "cidadeRegistro", "cartorioRegistro"] : []),
    ];

    const faltandoObrigatorios = camposObrigatoriosAtuais.filter((c) => estaVazio(form[c]));
    if (faltandoObrigatorios.length > 0) {
      alert("Preencha todos os campos obrigatórios.");
      return;
    }

    try {
      const paginaAntesSalvar = paginaAtual;

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const payload = {
        ...form,

        // ✅ ENVIA DINHEIRO COMO NÚMERO, SEM MÁSCARA
        valorCompraEscritura: unmaskMoney(form.valorCompraEscritura),
        valorCompraContrato: unmaskMoney(form.valorCompraContrato),

        areaTerreno: maskMetragem4BR(form.areaTerreno),
        areaConstruida: maskMetragem4BR(form.areaConstruida),
        metrosFrente: maskMetragem4BR(form.metrosFrente),
        metrosFundo: form.geometriaRegular === "Sim" ? maskMetragem4BR(form.metrosFundo) : "",
        metrosLadoDireito: form.geometriaRegular === "Sim" ? maskMetragem4BR(form.metrosLadoDireito) : "",
        metrosLadoEsquerdo: form.geometriaRegular === "Sim" ? maskMetragem4BR(form.metrosLadoEsquerdo) : "",
        linkGoogleMaps: form.linkGoogleMaps || null,
        prazoResolutiva: onlyDate(form.prazoResolutiva) || null,
      };

      console.log("Payload enviado:", payload);

      if (editandoId) {
        await axios.put(`/api/imoveis/${editandoId}`, payload, { headers });

        if (gedPendentes.length > 0) {
          const gedEnviado = await enviarGED(editandoId, gedPendentes);

          if (!gedEnviado) {
            alert("O imóvel foi atualizado, mas ocorreu uma falha ao enviar os novos arquivos GED.");
            return;
          }

          setGedPendentes([]);
        }

        alert("Imóvel atualizado com sucesso!");
      } else {
        const resp = await axios.post("/api/imoveis", payload, { headers });

        const novoId = resp.data?.id;

        alert(`Imóvel cadastrado com sucesso! Código: ${resp.data?.codigoGerado ?? ""}`);

        if (novoId && gedPendentes.length > 0) {
          const okGed = await enviarGED(novoId, gedPendentes);

          if (okGed) {
            setGedPendentes([]);
            alert("Anexos GED enviados!");
          } else {
            alert("Imóvel cadastrado, mas falhou ao enviar os anexos GED.");
          }
        }
      }

      const { data } = await axios.get("/api/imoveis", { headers });
      console.log("Dados retornados do backend:", data);
      aplicarListaUI(data);

      setPaginaAtual(paginaAntesSalvar);

      cancelarFormulario();
    } catch (err) {
      console.error("Erro ao salvar imóvel:", err?.response?.data ?? err);
      alert("Erro ao salvar imóvel. Verifique os dados.");
    }
  };

  // Paginação segura
  const totalRegistros = filtrados?.length || 0;

  const indexInicial = totalRegistros === 0 ? 0 : (paginaAtual - 1) * matriculasPorPagina;
  const indexFinal = totalRegistros === 0 ? 0 : Math.min(indexInicial + matriculasPorPagina, totalRegistros);
  const registrosPaginados = filtrados.slice(indexInicial, indexFinal);

  // SISTEMA DE PERMISSÕES
  const tokenPerm = localStorage.getItem("token");
  let tipoUsuario = "";

  if (tokenPerm) {
    const decoded = jwtDecode(tokenPerm);
    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["administrador"].includes(tipoUsuario);

  const isReadOnly = modoVisualizacao === true;

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        <h2>Matrículas de Imóveis</h2>

        <Box
          className="bloco-linha"
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              height: 52,
              width: "100%",
              border: "1px solid var(--ig-b-555555)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              px: 2,
            }}
          >
            <input
              type="text"
              placeholder="Filtrar Matrículas"
              value={busca}
              onChange={handleBusca}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ig-t-ffffff)",
                fontSize: "1rem",
              }}
            />

            <SearchIcon
              sx={{
                color: "var(--ig-t-9ca3af)",
                fontSize: 20,
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={limparFiltro}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={copiarTabela}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <FaCopy style={{ marginRight: 6 }} />
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={exportarCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>
        </Box>

        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o grid"
            >
              <FaChevronDown />
              Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o grid"
            >
              <FaChevronUp />
              Expandir
            </button>
          )}
        </div>

        {!gridMinimizado && (
          <div className="bloco-lista">
            {carregando ? (
              <p>Carregando...</p>
            ) : filtrados.length === 0 ? (
              <p>Não há registros para exibir</p>
            ) : (
              <>
                <table className="tabela-matriculas">
                  <colgroup>
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "22%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Apelido</th>
                      <th>Cidade</th>
                      <th>Complemento</th>
                      <th>Área (m²)</th>
                      <th>Valor Contrato</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrosPaginados.map((item) => (
                      <tr key={item.id}>
                        <td>{item.codigoMatricula}</td>
                        <td>{item.apelido}</td>
                        <td>{item.cidade}</td>
                        <td>{item.complemento}</td>
                        <td>{maskMetragemBR(item.areaTerreno ?? "", true)}</td>
                        <td>{formatMoneyBR(item.valorCompraContrato)}</td>

                        <td className="col-acoes">
                          <button className={`btn-ged-lista ${item.qtdArquivos > 0 ? "btn-ged-ok" : ""}`} onClick={() => abrirGED(item.id)} type="button">
                            GED
                            {item.qtdArquivos > 0 && <span className="ged-badge">{item.qtdArquivos}</span>}
                          </button>

                          {podeVisualizar && (
                            <button
                              className="btn-editar"
                              onClick={() => visualizarRegistro(item)}
                              aria-label={`Visualizar ${item.codigoMatricula}`}
                              type="button"
                            >
                              <FaEye />
                              Visualizar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <Box
                  className="rodape-matriculas-mui"
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "flex-end",
                    pt: 1,
                  }}
                >
                  <TablePagination
                    component="div"
                    count={filtrados.length}
                    page={paginaAtual - 1}
                    onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                    rowsPerPage={matriculasPorPagina}
                    onRowsPerPageChange={(e) => {
                      setMatriculasPorPagina(parseInt(e.target.value, 10));
                      setPaginaAtual(1);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Matrículas por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            backgroundColor: "var(--ig-f-111827) !important",
                            color: "var(--ig-t-ffffff)",

                            "& .MuiList-root": {
                              backgroundColor: "var(--ig-f-111827)",
                              padding: 0,
                            },

                            "& .MuiMenuItem-root": {
                              backgroundColor: "var(--ig-f-111827)",
                              color: "var(--ig-t-ffffff)",
                            },

                            "& .MuiMenuItem-root:hover": {
                              backgroundColor: "var(--ig-f-1f2937)",
                            },

                            "& .MuiMenuItem-root.Mui-selected": {
                              backgroundColor: "var(--ig-f-1f2937) !important",
                            },

                            "& .MuiMenuItem-root.Mui-selected:hover": {
                              backgroundColor: "#374151 !important",
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      fontSize: "0.8rem",
                      minHeight: 42,
                      color: "var(--ig-t-ffffff)",

                      borderTop: "none",
                      "&::before": {
                        display: "none",
                      },

                      "& .MuiToolbar-root": {
                        borderTop: "none",
                      },

                      "& .MuiTablePagination-toolbar": {
                        minHeight: 42,
                        paddingLeft: 0,
                        paddingRight: 0,
                      },

                      "& .MuiTablePagination-toolbar": {
                        minHeight: 42,
                        paddingLeft: 0,
                        paddingRight: 0,
                      },

                      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                        fontSize: "0.8rem",
                      },

                      "& .MuiTablePagination-selectIcon": {
                        color: "var(--ig-t-ffffff)",
                      },

                      "& .MuiTablePagination-actions button": {
                        color: "var(--ig-t-ffffff)",
                        width: "auto",
                      },

                      "& .MuiTablePagination-actions": {
                        display: "flex",
                        alignItems: "center",
                        "& .MuiTablePagination-root": {
                          borderTop: "none",
                        },
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </div>
        )}

        {modalGED && (
          <div className="modal-ged-overlay">
            <div className="modal-ged">
              <h3>Arquivos GED - Imóvel #{idSelecionadoGED}</h3>

              {arquivos.length === 0 ? (
                <p>Nenhum arquivo cadastrado para este imóvel.</p>
              ) : (
                <ul style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {arquivos.map((arq) => {
                    const arquivoId = arq.id || arq.nome;

                    return (
                      <li key={arquivoId} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ flex: 1 }}>{arq.nome}</span>

                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <button type="button" className="btn-visualizar-ged" onClick={() => visualizarArquivoGED(idSelecionadoGED, arquivoId, arq.nome)}>
                            <FaEye /> Visualizar
                          </button>

                          <button type="button" className="btn-baixar-ged" onClick={() => baixarArquivoGED(arquivoId, arq.nome)}>
                            Baixar
                          </button>

                          <button type="button" className="btn-excluir-ged" onClick={() => excluirArquivoGED(arquivoId)}>
                            <FaTrash /> Excluir
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              <button type="button" className="btn-fechar-ged" onClick={() => setModalGED(false)}>
                Fechar
              </button>
            </div>
          </div>
        )}
        {podeCadastrar || (podeVisualizar && modoVisualizacao) || (podeEditar && editandoId) ? (
          <div className="bloco-formulario">
            <form
              className="form-grid"
              onSubmit={(e) => {
                if (modoVisualizacao) {
                  e.preventDefault();
                  return;
                }
                handleSubmit(e);
              }}
            >
              <div className="form-row">
                <TextField
                  label="Apelido*"
                  name="apelido"
                  value={form.apelido}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  error={erroCampoMUI("apelido")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  select
                  label="Tipo de Documento do Imóvel*"
                  name="tipoDocumentoImovel"
                  value={form.tipoDocumentoImovel || ""}
                  onChange={(e) => {
                    const novoTipo = e.target.value;

                    setForm((prev) => ({
                      ...prev,
                      tipoDocumentoImovel: novoTipo,

                      // Ao deixar de ser MATRÍCULA, remove os dados
                      // específicos de registro que ficaram ocultos.
                      numeroMatricula: novoTipo === "MATRÍCULA" ? prev.numeroMatricula : "",

                      cidadeRegistro: novoTipo === "MATRÍCULA" ? prev.cidadeRegistro : "",

                      cartorioRegistro: novoTipo === "MATRÍCULA" ? prev.cartorioRegistro : "",
                    }));
                  }}
                  disabled={isReadOnly}
                  error={erroCampoMUI("tipoDocumentoImovel")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  SelectProps={menuPropsDark}
                >
                  <MenuItem value="">Selecione</MenuItem>
                  <MenuItem value="INSTRUMENTO PARTICULAR">INSTRUMENTO PARTICULAR</MenuItem>
                  <MenuItem value="USUCAPIÃO">USUCAPIÃO</MenuItem>
                  <MenuItem value="ESCRITURA">ESCRITURA</MenuItem>
                  <MenuItem value="MATRÍCULA">MATRÍCULA</MenuItem>
                </TextField>

                {form.tipoDocumentoImovel === "MATRÍCULA" && (
                  <>
                    <TextField
                      label="Número da Matrícula"
                      name="numeroMatricula"
                      value={form.numeroMatricula}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          numeroMatricula: maskNumeroMatricula(e.target.value),
                        }))
                      }
                      disabled={isReadOnly}
                      error={erroCampoMUI("numeroMatricula")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      select
                      label="Cidade de Registro"
                      name="cidadeRegistro"
                      value={form.cidadeRegistro}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          cidadeRegistro: e.target.value,
                        }))
                      }
                      disabled={isReadOnly}
                      error={erroCampoMUI("cidadeRegistro")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                      SelectProps={menuPropsDark}
                    >
                      <MenuItem value="">Selecione a Cidade</MenuItem>

                      {cidadesRegistro.map((cidade, i) => (
                        <MenuItem key={i} value={cidade}>
                          {cidade}
                        </MenuItem>
                      ))}
                    </TextField>
                  </>
                )}
              </div>

              <div className="form-row">
                {form.tipoDocumentoImovel === "MATRÍCULA" && (
                  <TextField
                    select
                    label="Cartório Registro"
                    name="cartorioRegistro"
                    value={form.cartorioRegistro}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        cartorioRegistro: e.target.value,
                      })
                    }
                    disabled={isReadOnly}
                    error={erroCampoMUI("cartorioRegistro")}
                    fullWidth
                    size="small"
                    sx={muiCampoSx}
                    SelectProps={menuPropsDark}
                  >
                    <MenuItem value="">Selecione o Cartório</MenuItem>

                    {cartorios.map((c, i) => (
                      <MenuItem key={i} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <TextField
                  select
                  label="Nome Proprietário Matrícula"
                  name="nomeProprietarioMatricula"
                  value={form.nomeProprietarioMatricula}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      nomeProprietarioMatricula: e.target.value,
                    })
                  }
                  disabled={isReadOnly}
                  error={erroCampoMUI("nomeProprietarioMatricula")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  SelectProps={menuPropsDark}
                >
                  <MenuItem value="">Selecione</MenuItem>

                  {proprietariosMatricula.map((p, i) => (
                    <MenuItem key={i} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Nome Proprietário Conceito"
                  name="nomeProprietarioConceito"
                  value={form.nomeProprietarioConceito}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      nomeProprietarioConceito: e.target.value,
                    })
                  }
                  disabled={isReadOnly}
                  error={erroCampoMUI("nomeProprietarioConceito")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  SelectProps={menuPropsDark}
                >
                  <MenuItem value="">Selecione</MenuItem>

                  {proprietariosMatricula.map((p, i) => (
                    <MenuItem key={i} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <div className="form-row">
                <TextField
                  select
                  label="Status"
                  name="status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  disabled={isReadOnly}
                  error={erroCampoMUI("status")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  SelectProps={menuPropsDark}
                >
                  <MenuItem value="">Selecione</MenuItem>

                  {statusList.map((s, i) => (
                    <MenuItem key={i} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Tipo"
                  name="tipo"
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  disabled={isReadOnly}
                  error={erroCampoMUI("tipo")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  SelectProps={menuPropsDark}
                >
                  <MenuItem value="">Selecione</MenuItem>
                  <MenuItem value="RURAL">RURAL</MenuItem>
                  <MenuItem value="URBANO">URBANO</MenuItem>
                </TextField>
              </div>

              <div
                className="form-row endereco-row-1"
                style={{
                  gridTemplateColumns: "170px 1.7fr 150px 1fr",
                }}
              >
                <TextField
                  label="CEP"
                  name="cep"
                  value={form.cep}
                  onChange={async (e) => {
                    if (isReadOnly) return;

                    const cepFormatado = maskCEP(e.target.value);

                    setForm((prev) => ({
                      ...prev,
                      cep: cepFormatado,
                    }));

                    if (cepFormatado.replace(/\D/g, "").length === 8) {
                      const dadosCEP = await buscarCEP(cepFormatado);

                      if (dadosCEP) {
                        setForm((prev) => ({
                          ...prev,
                          endereco: dadosCEP.endereco || prev.endereco,
                          bairro: dadosCEP.bairro || prev.bairro,
                          cidade: dadosCEP.cidade || prev.cidade,
                          uf: dadosCEP.uf || prev.uf,
                        }));
                      }
                    }
                  }}
                  disabled={isReadOnly}
                  error={erroCampoMUI("cep")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  label="Endereço*"
                  name="endereco"
                  value={form.endereco}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  error={erroCampoMUI("endereco")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  label="Número*"
                  name="numero"
                  value={form.numero}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  error={erroCampoMUI("numero")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  label="Bairro"
                  name="bairro"
                  value={form.bairro}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  error={erroCampoMUI("bairro")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <div
                className="form-row endereco-row-2"
                style={{
                  gridTemplateColumns: "260px 110px 1.8fr",
                }}
              >
                <TextField
                  label="Cidade"
                  name="cidade"
                  value={form.cidade}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  error={erroCampoMUI("cidade")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  label="UF"
                  name="uf"
                  value={form.uf}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  error={erroCampoMUI("uf")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <Box sx={{ position: "relative", width: "100%" }}>
                  <TextField
                    label="Complemento*"
                    name="complemento"
                    value={form.complemento}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    error={erroCampoMUI("complemento")}
                    fullWidth
                    size="small"
                    sx={{
                      ...muiCampoSx,
                      "& .MuiOutlinedInput-input": {
                        padding: "16.5px 42px 16.5px 14px",
                        color: "var(--ig-t-ffffff)",
                      },
                    }}
                  />

                  <span className="info-complemento">
                    <FaInfoCircle />

                    <span className="tooltip-complemento">
                      Informe detalhes para diferenciar o imóvel.
                      <br />
                      Exemplo: Bloco/Torre, Apto/Sala,
                      <br />
                      Casa 2, Lote 15, Quadra B.
                    </span>
                  </span>
                </Box>
              </div>

              <div
                className="form-row"
                style={{
                  gridTemplateColumns: form.linkGoogleMaps?.trim() ? "1fr auto" : "1fr",
                  alignItems: "center",
                }}
              >
                <TextField
                  label="Link Google Maps"
                  name="linkGoogleMaps"
                  value={form.linkGoogleMaps || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      linkGoogleMaps: e.target.value,
                    }))
                  }
                  placeholder="Cole aqui o link do Google Maps"
                  disabled={isReadOnly}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                {form.linkGoogleMaps?.trim() && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => navigator.clipboard.writeText(form.linkGoogleMaps)}
                      startIcon={<FaCopy />}
                      sx={{
                        minWidth: 120,
                        height: 52,
                        backgroundColor: "#2196f3",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "#1976d2",
                        },
                      }}
                    >
                      Copiar
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => window.open(form.linkGoogleMaps, "_blank")}
                      startIcon={<FaGlobe />}
                      sx={{
                        minWidth: 120,
                        height: 52,
                        backgroundColor: "#f97316",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "#ea580c",
                        },
                      }}
                    >
                      Abrir
                    </Button>
                  </Box>
                )}
              </div>

              <div className="form-row">
                <TextField
                  label="Latitude*"
                  name="latitude"
                  value={form.latitude}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      latitude: maskCoordinate(e.target.value),
                    })
                  }
                  disabled={isReadOnly}
                  error={erroCampoMUI("latitude")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  label="Longitude*"
                  name="longitude"
                  value={form.longitude}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      longitude: maskCoordinate(e.target.value),
                    })
                  }
                  disabled={isReadOnly}
                  error={erroCampoMUI("longitude")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Nome Vendedor"
                  name="nomeVendedor"
                  value={form.nomeVendedor}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  error={erroCampoMUI("nomeVendedor")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Data Compra Escritura"
                  type="date"
                  name="dataCompraEscritura"
                  value={form.dataCompraEscritura}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      dataCompraEscritura: e.target.value,
                    })
                  }
                  disabled={isReadOnly}
                  error={erroCampoMUI("dataCompraEscritura")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />

                <TextField
                  label="Valor Compra Escritura"
                  name="valorCompraEscritura"
                  value={form.valorCompraEscritura}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      valorCompraEscritura: maskMoney(e.target.value),
                    })
                  }
                  disabled={isReadOnly}
                  error={erroCampoMUI("valorCompraEscritura")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Data Compra Contrato"
                  type="date"
                  name="dataCompraContrato"
                  value={form.dataCompraContrato}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      dataCompraContrato: e.target.value,
                    })
                  }
                  disabled={isReadOnly}
                  error={erroCampoMUI("dataCompraContrato")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />

                <TextField
                  label="Valor Compra Contrato"
                  name="valorCompraContrato"
                  value={form.valorCompraContrato}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      valorCompraContrato: maskMoney(e.target.value),
                    })
                  }
                  disabled={isReadOnly}
                  error={erroCampoMUI("valorCompraContrato")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Área Terreno (m²)"
                  name="areaTerreno"
                  value={form.areaTerreno}
                  onChange={handleMetragemChange("areaTerreno")}
                  onBlur={handleMetragemBlur("areaTerreno")}
                  disabled={isReadOnly}
                  error={erroCampoMUI("areaTerreno")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                <TextField
                  label="Área Construída (m²)"
                  name="areaConstruida"
                  value={form.areaConstruida}
                  onChange={handleMetragemChange("areaConstruida")}
                  onBlur={handleMetragemBlur("areaConstruida")}
                  disabled={isReadOnly}
                  error={erroCampoMUI("areaConstruida")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <div className="form-row">
                <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                  <span style={{ marginBottom: "8px", fontWeight: 600 }}>Geometria Regular</span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                      minHeight: "44px",
                    }}
                  >
                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="radio"
                        name="geometriaRegular"
                        value="Não"
                        checked={form.geometriaRegular === "Não"}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            geometriaRegular: e.target.value,
                            metrosFundo: "",
                            metrosLadoDireito: "",
                            metrosLadoEsquerdo: "",
                          })
                        }
                        disabled={isReadOnly}
                      />
                      Não
                    </label>

                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="radio"
                        name="geometriaRegular"
                        value="Sim"
                        checked={form.geometriaRegular === "Sim"}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            geometriaRegular: e.target.value,
                          })
                        }
                        disabled={isReadOnly}
                      />
                      Sim
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <TextField
                  label="Metros de Frente (m)"
                  name="metrosFrente"
                  value={form.metrosFrente}
                  onChange={handleMetragemChange("metrosFrente")}
                  onBlur={handleMetragemBlur("metrosFrente")}
                  disabled={isReadOnly}
                  error={erroCampoMUI("metrosFrente")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />

                {form.geometriaRegular === "Sim" ? (
                  <>
                    <TextField
                      label="Metros de Fundo (m)"
                      name="metrosFundo"
                      value={form.metrosFundo}
                      onChange={handleMetragemChange("metrosFundo")}
                      onBlur={handleMetragemBlur("metrosFundo")}
                      disabled={isReadOnly}
                      error={erroCampoMUI("metrosFundo")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Metros Lado Direito (m)"
                      name="metrosLadoDireito"
                      value={form.metrosLadoDireito}
                      onChange={handleMetragemChange("metrosLadoDireito")}
                      onBlur={handleMetragemBlur("metrosLadoDireito")}
                      disabled={isReadOnly}
                      error={erroCampoMUI("metrosLadoDireito")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />

                    <TextField
                      label="Metros Lado Esquerdo (m)"
                      name="metrosLadoEsquerdo"
                      value={form.metrosLadoEsquerdo}
                      onChange={handleMetragemChange("metrosLadoEsquerdo")}
                      onBlur={handleMetragemBlur("metrosLadoEsquerdo")}
                      disabled={isReadOnly}
                      error={erroCampoMUI("metrosLadoEsquerdo")}
                      fullWidth
                      size="small"
                      sx={muiCampoSx}
                    />
                  </>
                ) : null}
              </div>

              <div className="form-row">
                <TextField
                  label="Prazo da Resolutiva"
                  type="date"
                  name="prazoResolutiva"
                  value={form.prazoResolutiva ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      prazoResolutiva: e.target.value,
                    })
                  }
                  disabled={isReadOnly}
                  error={erroCampoMUI("prazoResolutiva")}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </div>

              <div className="form-row">
                <TextField
                  label="Observação"
                  name="observacao"
                  value={form.observacao}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  error={erroCampoMUI("observacao")}
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                />
              </div>

              <input ref={inputGedRef} id="uploadGED" type="file" multiple style={{ display: "none" }} onChange={selecionarArquivosGED} />

              {!isReadOnly && gedPendentes.length > 0 && (
                <div
                  style={{
                    width: "100%",
                    marginTop: "12px",
                    marginBottom: "16px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      marginBottom: "10px",
                      color: "var(--ig-t-ffffff)",
                      fontWeight: 600,
                    }}
                  >
                    Arquivos pendentes para anexar
                  </label>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {gedPendentes.map((arquivo, index) => (
                      <div
                        key={`${arquivo.name}-${arquivo.size}-${arquivo.lastModified}-${index}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) 124px",
                          alignItems: "center",
                          gap: "16px",
                          width: "100%",
                        }}
                      >
                        <input
                          type="text"
                          value={arquivo.name}
                          readOnly
                          title={arquivo.name}
                          style={{
                            width: "100%",
                            height: "52px",
                            boxSizing: "border-box",
                            border: "1px solid var(--ig-b-555555)",
                            borderRadius: "4px",
                            backgroundColor: "transparent",
                            color: "var(--ig-t-ffffff)",
                            padding: "0 14px",
                            fontSize: "1rem",
                            outline: "none",
                          }}
                        />

                        <button
                          type="button"
                          onClick={() => {
                            setGedPendentes((arquivosAtuais) => arquivosAtuais.filter((_, arquivoIndex) => arquivoIndex !== index));
                          }}
                          style={{
                            width: "124px",
                            height: "54px",
                            border: "none",
                            borderRadius: "10px",
                            backgroundColor: "#d2262a",
                            color: "#ffffff",
                            fontSize: "1rem",
                            fontWeight: 500,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="iptu-botoes">
                {modoVisualizacao ? (
                  <>
                    {podeEditar && (
                      <button
                        type="button"
                        className="iptu-btn-acao btn-cadastrar"
                        onClick={(e) => {
                          e.preventDefault();
                          const id = visualizandoId;
                          if (!id) return;
                          const item = registros.find((r) => r.id === id);
                          if (!item) return;
                          editarRegistro(item);
                        }}
                      >
                        <FaEdit className="iptu-btn-icone" />
                        Editar
                      </button>
                    )}

                    {podeExcluir && (
                      <button
                        type="button"
                        className="iptu-btn-acao btn-excluir"
                        onClick={(e) => {
                          e.preventDefault();
                          const id = visualizandoId;
                          if (!id) return;
                          excluirRegistro(id);
                        }}
                      >
                        <FaTrash className="iptu-btn-icone" />
                        Excluir
                      </button>
                    )}

                    <button
                      type="button"
                      className="iptu-btn-acao btn-acao"
                      onClick={(e) => {
                        e.preventDefault();
                        cancelarFormulario();
                      }}
                    >
                      <FaTimes className="iptu-btn-icone" />
                      Cancelar
                    </button>

                    <button
                      type="button"
                      className={`iptu-btn-acao btn-ged ${(registros.find((r) => r.id === visualizandoId)?.qtdArquivos || 0) > 0 ? "btn-ged-ok" : ""}`}
                      onClick={() => abrirGED(visualizandoId)}
                    >
                      <FaPaperclip className="iptu-btn-icone" />
                      GED
                      {(() => {
                        const reg = registros.find((r) => r.id === visualizandoId);
                        return reg?.qtdArquivos > 0 ? <span className="ged-badge">{reg.qtdArquivos}</span> : null;
                      })()}
                    </button>
                  </>
                ) : editandoId ? (
                  <>
                    <button type="submit" className="iptu-btn-acao btn-cadastrar">
                      <FaEdit className="iptu-btn-icone" />
                      Atualizar
                    </button>

                    {podeExcluir && (
                      <button
                        type="button"
                        className="iptu-btn-acao btn-excluir"
                        onClick={(e) => {
                          e.preventDefault();
                          excluirRegistro(editandoId);
                        }}
                      >
                        <FaTrash className="iptu-btn-icone" />
                        Excluir
                      </button>
                    )}

                    <button
                      type="button"
                      className="iptu-btn-acao btn-acao"
                      onClick={(e) => {
                        e.preventDefault();
                        cancelarFormulario();
                      }}
                    >
                      <FaTimes className="iptu-btn-icone" />
                      Cancelar
                    </button>

                    <button
                      type="button"
                      className={`iptu-btn-acao btn-ged ${
                        (registros.find((r) => r.id === editandoId)?.qtdArquivos || 0) > 0 || gedPendentes.length > 0 ? "btn-ged-ok" : ""
                      }`}
                      onClick={(event) => {
                        event.preventDefault();
                        abrirSeletorGED();
                      }}
                    >
                      <FaPaperclip className="iptu-btn-icone" />
                      GED
                      {(() => {
                        const registro = registros.find((item) => item.id === editandoId);

                        const quantidadeSalva = Number(registro?.qtdArquivos) || 0;

                        const quantidadeTotal = quantidadeSalva + gedPendentes.length;

                        return quantidadeTotal > 0 ? <span className="ged-badge">{quantidadeTotal}</span> : null;
                      })()}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="submit" className="iptu-btn-acao btn-cadastrar">
                      <FaSave className="iptu-btn-icone" />
                      Cadastrar
                    </button>

                    <button
                      type="button"
                      className="iptu-btn-acao btn-acao"
                      onClick={(e) => {
                        e.preventDefault();
                        cancelarFormulario();
                      }}
                    >
                      <FaTimes className="iptu-btn-icone" />
                      Cancelar
                    </button>

                    <button
                      type="button"
                      className={`iptu-btn-acao btn-ged ${gedPendentes.length > 0 ? "btn-ged-ok" : ""}`}
                      onClick={(event) => {
                        event.preventDefault();
                        abrirSeletorGED();
                      }}
                    >
                      <FaPaperclip className="iptu-btn-icone" />
                      GED
                      {gedPendentes.length > 0 && <span className="ged-badge">{gedPendentes.length}</span>}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        ) : (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default CadastroImoveis;
